import { Component, Input, OnInit, OnDestroy, ViewChild, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import moment from 'moment';

interface ShuttleMetrics {
  totalBaggageArrival: number;
  totalBaggageDeparture: number;
  totalBaggageTotal: number;
  totalCarsArrival: number;
  totalCarsDeparture: number;
  totalCarsTotal: number;
  totalFlightsArrival: number;
  totalFlightsDeparture: number;
  totalFlightsTotal: number;
  totalOrdersArrival: number;
  totalOrdersDeparture: number;
  totalOrdersTotal: number;
  totalPassengersArrival: number;
  totalPassengersDeparture: number;
  totalPassengersTotal: number;
}

interface ProcessedShuttleData {
  labels: string[];
  datasets: ShuttleMetrics[];
}

@Component({
  selector: 'app-shuttle-compare',
  standalone: false,
  templateUrl: './shuttle-compare.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './shuttle-compare.component.css'
})
export class ShuttleCompareComponent implements OnInit, OnDestroy {
  @Input() organization: MyConfigOrganization;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective<'line'>;
  @Output() incrementRequests = new EventEmitter<void>();
  @Output() decrementRequests = new EventEmitter<void>();

  isLoading = false;
  timeAxisMode: 'days' | 'hours' = 'days';
  primaryYear: number = new Date().getFullYear();
  comparisonYear: number = new Date().getFullYear() - 1;

  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  minShuttleDate: Date | null = null;
  maxShuttleDate: Date | null = null;

  availableYears: number[] = [];
  combinedChartData: ChartData<'line'> = { labels: [], datasets: [] };

  private primaryShuttleData: any = null;
  private comparisonShuttleData: any = null;
  private destroy$ = new Subject<void>();

  public readonly combinedChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { title: { display: true, text: 'Timeline' } },
      y: { beginAtZero: true, title: { display: true, text: 'Count' } }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15
        }
      },
      title: {
        display: true,
        text: `Shuttle Operations Comparison - ${this.primaryYear} vs ${this.comparisonYear}`,
        font: { size: 16, weight: 'bold' }
      },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  constructor(private backOfficeService: BackOfficeService) { }

  ngOnInit(): void {
    this.initializeYears();
    this.setInitialsDates();
    this.loadAllData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeYears(): void {
    const currentYear = new Date().getFullYear();
    // Generate last 5 years
    for (let i = 0; i < 5; i++) {
      this.availableYears.push(currentYear - i);
    }
  }

  setInitialsDates(): void {
    const oneWeekMs = 24 * 60 * 60 * 1000 * 7;
    this.selectedStartDate = new Date();
    this.selectedEndDate = new Date(this.selectedStartDate.getTime() + oneWeekMs);
  }

  onDateRangeChange(): void {
    if (this.selectedStartDate && this.selectedEndDate) {
      const diffDays = moment(this.selectedEndDate).diff(moment(this.selectedStartDate), 'days');

      if (Math.abs(diffDays) < 1) {
        this.selectedEndDate = moment(this.selectedStartDate).add(1, 'day').toDate();
      }
      this.selectedStartDate = moment(this.selectedStartDate).startOf('day').toDate();
      this.selectedEndDate = moment(this.selectedEndDate).endOf('day').toDate();

      // Clear existing data to force reload
      this.primaryShuttleData = null;
      this.comparisonShuttleData = null;
      this.combinedChartData = { labels: [], datasets: [] };

      // Load new data for the selected date range
      this.loadAllData();
    }
  }

  switchTimeAxis(event: any): void {
    this.timeAxisMode = event.value || this.timeAxisMode;
    this.processAllShuttleData();
  }

  onPrimaryYearChange(): void {
    // Clear primary data to force reload
    this.primaryShuttleData = null;
    this.loadPrimaryYearData();
    this.updateChartTitle();
  }

  onComparisonYearChange(): void {
    // Clear comparison data to force reload
    this.comparisonShuttleData = null;
    this.loadComparisonYearData();
    this.updateChartTitle();
  }

  private updateChartTitle(): void {
    if (this.combinedChartOptions?.plugins?.title) {
      this.combinedChartOptions.plugins.title.text = `Shuttle Operations Comparison - ${this.primaryYear} vs ${this.comparisonYear}`;
    }
  }

  private loadAllData(): void {
    this.loadPrimaryYearData();
    this.loadComparisonYearData();
  }

  private loadPrimaryYearData(): void {
    if (!this.selectedStartDate || !this.selectedEndDate) return;

    // Calculate date range for primary year
    const baseYear = this.selectedStartDate.getFullYear();
    const yearDiff = this.primaryYear - baseYear;

    const primaryStartDate = moment(this.selectedStartDate).add(yearDiff, 'years');
    const primaryEndDate = moment(this.selectedEndDate).add(yearDiff, 'years');

    console.log(`Loading primary year data: ${this.primaryYear}`, {
      originalRange: `${this.selectedStartDate} - ${this.selectedEndDate}`,
      adjustedRange: `${primaryStartDate.format()} - ${primaryEndDate.format()}`
    });

    this.executeRequest(
      this.backOfficeService.getDashBoardPlanningData(this.organization?.organizationId,
        primaryStartDate.format('YYYY-MM-DDTHH:mm:ss'),
        primaryEndDate.format('YYYY-MM-DDTHH:mm:ss')),
      (data: any) => {
        this.primaryShuttleData = data;
        this.processAllShuttleData();
      }
    );
  }

  private loadComparisonYearData(): void {
    if (!this.selectedStartDate || !this.selectedEndDate) return;

    // Calculate date range for comparison year
    const baseYear = this.selectedStartDate.getFullYear();
    const yearDiff = this.comparisonYear - baseYear;

    const comparisonStartDate = moment(this.selectedStartDate).add(yearDiff, 'years');
    const comparisonEndDate = moment(this.selectedEndDate).add(yearDiff, 'years');

    console.log(`Loading comparison year data: ${this.comparisonYear}`, {
      originalRange: `${this.selectedStartDate} - ${this.selectedEndDate}`,
      adjustedRange: `${comparisonStartDate.format()} - ${comparisonEndDate.format()}`
    });

    this.executeRequest(
      this.backOfficeService.getDashBoardPlanningData(
        this.organization?.organizationId,
        comparisonStartDate.format('YYYY-MM-DDTHH:mm:ss'),
        comparisonEndDate.format('YYYY-MM-DDTHH:mm:ss')),
      (data: any) => {
        this.comparisonShuttleData = data;
        this.processAllShuttleData();
      }
    );
  }

  private processAllShuttleData(): void {
    // Only process if we have both datasets
    if (!this.primaryShuttleData || !this.comparisonShuttleData) return;

    const primaryProcessed = this.processShuttleDataForYear(this.primaryShuttleData);
    const comparisonProcessed = this.processShuttleDataForYear(this.comparisonShuttleData);

    this.combinedChartData = this.createCombinedChartData(primaryProcessed, comparisonProcessed);
    this.updateChart();
  }

  private processShuttleDataForYear(shuttleData: any): ProcessedShuttleData {
    if (!shuttleData?.plannedShuttles?.orderShuttlePlanDays) {
      return { labels: [], datasets: [] };
    }

    return this.timeAxisMode === 'days'
      ? this.processByDays(shuttleData)
      : this.processByHours(shuttleData);
  }

  private processByDays(shuttleData: any): ProcessedShuttleData {
    const labels: string[] = [];
    const datasets: ShuttleMetrics[] = [];

    shuttleData.plannedShuttles.orderShuttlePlanDays.forEach((day: any) => {
      const metrics = day.orderShuttlePlanHours.reduce((acc: any, hour: any) => {
        return {
          totalBaggageArrival: acc.totalBaggageArrival + (hour.orderShuttleArrival.totalBaggage || 0),
          totalBaggageDeparture: acc.totalBaggageDeparture + (hour.orderShuttleDeparture.totalBaggage || 0),
          totalBaggageTotal: acc.totalBaggageTotal + (hour.orderShuttleArrival.totalBaggage || 0) + (hour.orderShuttleDeparture.totalBaggage || 0),
          totalCarsArrival: acc.totalCarsArrival + (hour.orderShuttleArrival.totalCars || 0),
          totalCarsDeparture: acc.totalCarsDeparture + (hour.orderShuttleDeparture.totalCars || 0),
          totalCarsTotal: acc.totalCarsTotal + (hour.orderShuttleArrival.totalCars || 0) + (hour.orderShuttleDeparture.totalCars || 0),
          // totalCarsArrival: 0,
          // totalCarsDeparture: 0,
          // totalCarsTotal: 0,
          totalFlightsArrival: acc.totalFlightsArrival + (hour.orderShuttleArrival.totalFlights || 0),
          totalFlightsDeparture: acc.totalFlightsDeparture + (hour.orderShuttleDeparture.totalFlights || 0),
          totalFlightsTotal: acc.totalFlightsTotal + (hour.orderShuttleArrival.totalFlights || 0) + (hour.orderShuttleDeparture.totalFlights || 0),
          // totalOrdersArrival: acc.totalOrdersArrival + (hour.orderShuttleArrival.totalOrders || 0),
          // totalOrdersDeparture: acc.totalOrdersDeparture + (hour.orderShuttleDeparture.totalOrders || 0),
          // totalOrdersTotal: acc.totalOrdersTotal + (hour.orderShuttleArrival.totalOrders || 0) + (hour.orderShuttleDeparture.totalOrders || 0),
          totalOrdersArrival: 0,
          totalOrdersDeparture: 0,
          totalOrdersTotal: 0,
          totalPassengersArrival: acc.totalPassengersArrival + (hour.orderShuttleArrival.totalPassengers || 0),
          totalPassengersDeparture: acc.totalPassengersDeparture + (hour.orderShuttleDeparture.totalPassengers || 0),
          totalPassengersTotal: acc.totalPassengersTotal + (hour.orderShuttleArrival.totalPassengers || 0) + (hour.orderShuttleDeparture.totalPassengers || 0)
        };
      }, this.getEmptyMetrics());
      labels.push(new Date(day.dayDate).toLocaleDateString());
      datasets.push(metrics);
    });
    return { labels, datasets };
  }

  private processByHours(shuttleData: any): ProcessedShuttleData {
    const labels: string[] = [];
    const datasets: ShuttleMetrics[] = [];

    shuttleData.plannedShuttles.orderShuttlePlanDays.forEach((day: any) => {
      day.orderShuttlePlanHours.forEach((hour: any) => {
        labels.push(new Date(hour.fromDate).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }));
        datasets.push({
          totalBaggageArrival: hour.orderShuttleArrival.totalBaggage || 0,
          totalBaggageDeparture: hour.orderShuttleDeparture.totalBaggage || 0,
          totalBaggageTotal: (hour.orderShuttleArrival.totalBaggage || 0) + (hour.orderShuttleDeparture.totalBaggage || 0),
          totalCarsArrival: hour.orderShuttleArrival.totalCars || 0,
          totalCarsDeparture: hour.orderShuttleDeparture.totalCars || 0,
          totalCarsTotal: (hour.orderShuttleArrival.totalCars || 0) + (hour.orderShuttleDeparture.totalCars || 0),
          // totalCarsArrival: 0,
          // totalCarsDeparture: 0,
          // totalCarsTotal: 0,
          totalFlightsArrival: hour.orderShuttleArrival.totalFlights || 0,
          totalFlightsDeparture: hour.orderShuttleDeparture.totalFlights || 0,
          totalFlightsTotal: (hour.orderShuttleArrival.totalFlights || 0) + (hour.orderShuttleDeparture.totalFlights || 0),
          // totalOrdersArrival: hour.orderShuttleArrival.totalOrders || 0,
          // totalOrdersDeparture: hour.orderShuttleDeparture.totalOrders || 0,
          // totalOrdersTotal: (hour.orderShuttleArrival.totalOrders || 0) + (hour.orderShuttleDeparture.totalOrders || 0),
          totalOrdersArrival: 0,
          totalOrdersDeparture: 0,
          totalOrdersTotal: 0,
          totalPassengersArrival: hour.orderShuttleArrival.totalPassengers || 0,
          totalPassengersDeparture: hour.orderShuttleDeparture.totalPassengers || 0,
          totalPassengersTotal: (hour.orderShuttleArrival.totalPassengers || 0) + (hour.orderShuttleDeparture.totalPassengers || 0)
        });
      });
    });
    return { labels, datasets };
  }

  private createCombinedChartData(primaryData: ProcessedShuttleData, comparisonData: ProcessedShuttleData): ChartData<'line'> {
    // Use the primary data labels as the base
    const labels = primaryData.labels;

    const datasets = [
      // Primary Year - Passengers Arrival (Solid)
      {
        label: `${this.primaryYear} - Passengers (Arrival)`,
        data: primaryData.datasets.map(d => d.totalPassengersArrival),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        tension: 0.1,
        borderWidth: 2,
        // Solid line for primary year
      },
      // Primary Year - Passengers Departure (Solid)
      {
        label: `${this.primaryYear} - Passengers (Departure)`,
        data: primaryData.datasets.map(d => d.totalPassengersDeparture),
        borderColor: 'rgb(255, 159, 164)',
        backgroundColor: 'rgba(255, 159, 164, 0.1)',
        tension: 0.1,
        borderWidth: 2,
        // Solid line for primary year
      },
      // // Primary Year - Flights Arrival (Solid)
      // {
      //   label: `${this.primaryYear} - Flights (Arrival)`,
      //   data: primaryData.datasets.map(d => d.totalFlightsArrival),
      //   borderColor: 'rgb(75, 192, 192)',
      //   backgroundColor: 'rgba(75, 192, 192, 0.1)',
      //   tension: 0.1,
      //   borderWidth: 2,
      //   // Solid line for primary year
      // },
      // // Primary Year - Flights Departure (Solid)
      // {
      //   label: `${this.primaryYear} - Flights (Departure)`,
      //   data: primaryData.datasets.map(d => d.totalFlightsDeparture),
      //   borderColor: 'rgb(115, 212, 212)',
      //   backgroundColor: 'rgba(115, 212, 212, 0.1)',
      //   tension: 0.1,
      //   borderWidth: 2,
      //   // Solid line for primary year
      // },

      // Comparison Year - Passengers Arrival (Dashed)
      {
        label: `${this.comparisonYear} - Passengers (Arrival)`,
        data: comparisonData.datasets.map(d => d.totalPassengersArrival),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        tension: 0.1,
        borderWidth: 2,
        borderDash: [8, 4] // Dashed line for comparison year
      },
      // Comparison Year - Passengers Departure (Dashed)
      {
        label: `${this.comparisonYear} - Passengers (Departure)`,
        data: comparisonData.datasets.map(d => d.totalPassengersDeparture),
        borderColor: 'rgb(255, 159, 164)',
        backgroundColor: 'rgba(255, 159, 164, 0.1)',
        tension: 0.1,
        borderWidth: 2,
        borderDash: [8, 4] // Dashed line for comparison year
      },
      // // Comparison Year - Flights Arrival (Dashed)
      // {
      //   label: `${this.comparisonYear} - Flights (Arrival)`,
      //   data: comparisonData.datasets.map(d => d.totalFlightsArrival),
      //   borderColor: 'rgb(75, 192, 192)',
      //   backgroundColor: 'rgba(75, 192, 192, 0.1)',
      //   tension: 0.1,
      //   borderWidth: 2,
      //   borderDash: [8, 4] // Dashed line for comparison year
      // },
      // // Comparison Year - Flights Departure (Dashed)
      // {
      //   label: `${this.comparisonYear} - Flights (Departure)`,
      //   data: comparisonData.datasets.map(d => d.totalFlightsDeparture),
      //   borderColor: 'rgb(115, 212, 212)',
      //   backgroundColor: 'rgba(115, 212, 212, 0.1)',
      //   tension: 0.1,
      //   borderWidth: 2,
      //   borderDash: [8, 4] // Dashed line for comparison year
      // },

      // Commented out - Ready to uncomment when needed
      // Primary Year - Cars Arrival (Solid)
      {
        label: `${this.primaryYear} - Cars (Arrival)`,
        data: primaryData.datasets.map(d => d.totalCarsArrival),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        tension: 0.1,
        borderWidth: 2,
        // Solid line for primary year
      },
      // Primary Year - Cars Departure (Solid)
      {
        label: `${this.primaryYear} - Cars (Departure)`,
        data: primaryData.datasets.map(d => d.totalCarsDeparture),
        borderColor: 'rgb(116, 193, 255)',
        backgroundColor: 'rgba(116, 193, 255, 0.1)',
        tension: 0.1,
        borderWidth: 2,
        // Solid line for primary year
      },
      // Comparison Year - Cars Arrival (Dashed)
      {
        label: `${this.comparisonYear} - Cars (Arrival)`,
        data: comparisonData.datasets.map(d => d.totalCarsArrival),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        tension: 0.1,
        borderWidth: 2,
        borderDash: [8, 4] // Dashed line for comparison year
      },
      // Comparison Year - Cars Departure (Dashed)
      {
        label: `${this.comparisonYear} - Cars (Departure)`,
        data: comparisonData.datasets.map(d => d.totalCarsDeparture),
        borderColor: 'rgb(116, 193, 255)',
        backgroundColor: 'rgba(116, 193, 255, 0.1)',
        tension: 0.1,
        borderWidth: 2,
        borderDash: [8, 4] // Dashed line for comparison year
      },

      // // Primary Year - Orders Arrival (Solid)
      // {
      //   label: `${this.primaryYear} - Orders (Arrival)`,
      //   data: primaryData.datasets.map(d => d.totalOrdersArrival),
      //   borderColor: 'rgb(153, 102, 255)',
      //   backgroundColor: 'rgba(153, 102, 255, 0.1)',
      //   tension: 0.1,
      //   borderWidth: 2,
      //   // Solid line for primary year
      // },
      // // Primary Year - Orders Departure (Solid)
      // {
      //   label: `${this.primaryYear} - Orders (Departure)`,
      //   data: primaryData.datasets.map(d => d.totalOrdersDeparture),
      //   borderColor: 'rgb(183, 142, 255)',
      //   backgroundColor: 'rgba(183, 142, 255, 0.1)',
      //   tension: 0.1,
      //   borderWidth: 2,
      //   // Solid line for primary year
      // },
      // // Comparison Year - Orders Arrival (Dashed)
      // {
      //   label: `${this.comparisonYear} - Orders (Arrival)`,
      //   data: comparisonData.datasets.map(d => d.totalOrdersArrival),
      //   borderColor: 'rgb(153, 102, 255)',
      //   backgroundColor: 'rgba(153, 102, 255, 0.1)',
      //   tension: 0.1,
      //   borderWidth: 2,
      //   borderDash: [8, 4] // Dashed line for comparison year
      // },
      // // Comparison Year - Orders Departure (Dashed)
      // {
      //   label: `${this.comparisonYear} - Orders (Departure)`,
      //   data: comparisonData.datasets.map(d => d.totalOrdersDeparture),
      //   borderColor: 'rgb(183, 142, 255)',
      //   backgroundColor: 'rgba(183, 142, 255, 0.1)',
      //   tension: 0.1,
      //   borderWidth: 2,
      //   borderDash: [8, 4] // Dashed line for comparison year
      // }
    ];

    return { labels, datasets };
  }

  private getEmptyMetrics(): ShuttleMetrics {
    return {
      totalBaggageArrival: 0,
      totalBaggageDeparture: 0,
      totalBaggageTotal: 0,
      totalCarsArrival: 0,
      totalCarsDeparture: 0,
      totalCarsTotal: 0,
      totalFlightsArrival: 0,
      totalFlightsDeparture: 0,
      totalFlightsTotal: 0,
      totalOrdersArrival: 0,
      totalOrdersDeparture: 0,
      totalOrdersTotal: 0,
      totalPassengersArrival: 0,
      totalPassengersDeparture: 0,
      totalPassengersTotal: 0
    };
  }

  private executeRequest<T>(request: any, onSuccess: (data: T) => void): void {
    this.incrementRequests.emit();
    this.isLoading = true;
    request.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: T) => {
          onSuccess(data);
          this.decrementRequests.emit();
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('API request failed:', error);
          this.decrementRequests.emit();
          this.isLoading = false;
        }
      });
  }

  private updateChart(): void {
    this.chart?.update();
  }
}
